import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, AlertTriangle, ArrowRight, CalendarClock, CheckCircle2,
  Clock, Hospital, Inbox, Loader2, MapPin, Plus, Stethoscope, X,
  Sparkles, Ban, ShieldAlert, ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { plannerNavItems } from '../components/brand/navItems';
import { useLocation } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const SERVICE_OPTIONS = [
  { v: 'HOME_HEALTH_RN', l: 'Skilled nursing (RN)' },
  { v: 'HOME_HEALTH_LPN', l: 'Skilled nursing (LPN)' },
  { v: 'PT', l: 'Physical therapy' },
  { v: 'OT', l: 'Occupational therapy' },
  { v: 'ST', l: 'Speech therapy' },
  { v: 'CNA', l: 'CNA / Aide' },
  { v: 'HOSPICE', l: 'Hospice / palliative' },
];

const URGENCY_OPTIONS = [
  { v: 'routine', l: 'Routine', tone: 'bg-emerald-100 text-emerald-700' },
  { v: 'priority', l: 'Priority', tone: 'bg-amber-100 text-amber-700' },
  { v: 'urgent', l: 'Urgent', tone: 'bg-red-100 text-red-700' },
];

const PAYER_CLASSES = ['medicare', 'medicare_advantage', 'medicaid', 'commercial', 'tricare', 'self_pay'];

const StatusPill = ({ status }) => {
  const map = {
    matching:                { tone: 'bg-blue-100 text-blue-800', label: 'Matching' },
    matched:                 { tone: 'bg-emerald-100 text-emerald-800', label: 'Matched' },
    first_visit_committed:   { tone: 'bg-emerald-200 text-emerald-900', label: 'First visit booked' },
    escalated:               { tone: 'bg-red-100 text-red-800', label: 'Escalated' },
    cancelled:               { tone: 'bg-slate-200 text-slate-700', label: 'Cancelled' },
    closed:                  { tone: 'bg-slate-200 text-slate-700', label: 'Closed' },
    intake:                  { tone: 'bg-slate-100 text-slate-600', label: 'Intake' },
  };
  const m = map[status] || { tone: 'bg-slate-100 text-slate-700', label: status };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${m.tone}`} data-testid={`spine-status-${status}`}>
      {m.label}
    </span>
  );
};

const StatTile = ({ icon: Icon, label, value, tone, testId }) => (
  <div className="cn-card rounded-2xl p-5" data-testid={testId}>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${tone}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="text-3xl font-display font-semibold text-cn-text">{value}</div>
    <div className="text-sm text-cn-text-muted mt-0.5">{label}</div>
  </div>
);

const ReferralIntakeModal = ({ open, onClose, onCreated, orgs }) => {
  const { getAuthHeader } = useAuth();
  const empty = {
    org_id: orgs?.[0]?.id || '',
    discharge_date: '',
    ordered_services: [],
    diagnosis_codes: '',
    payer: '',
    payer_class: 'commercial',
    urgency: 'routine',
    state: '',
    risk_factors: '',
    patient: {
      mrn: '', first_name: '', last_name: '', dob: '',
      address_line_1: '', zip5: '', phone: '', member_id: '',
    },
  };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (orgs?.[0]?.id && !form.org_id) {
      setForm((f) => ({ ...f, org_id: orgs[0].id }));
    }
  }, [orgs]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setPatient = (k) => (e) => setForm((f) => ({ ...f, patient: { ...f.patient, [k]: e?.target ? e.target.value : e } }));
  const toggleService = (v) => setForm((f) => ({
    ...f,
    ordered_services: f.ordered_services.includes(v)
      ? f.ordered_services.filter((x) => x !== v)
      : [...f.ordered_services, v],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.org_id) { toast.error('Pick an originating organization'); return; }
    if (!form.ordered_services.length) { toast.error('At least one service is required'); return; }
    if (!form.state) { toast.error('State is required'); return; }
    setSubmitting(true);
    try {
      const body = {
        ...form,
        diagnosis_codes: form.diagnosis_codes.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean),
        risk_factors: form.risk_factors.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean),
        patient: { ...form.patient },
      };
      const res = await axios.post(`${API}/spine/referrals`, body, getAuthHeader());
      toast.success(`Referral created — status: ${res.data?.status || 'matching'}`);
      setForm(empty);
      onCreated();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 402 && detail?.entitlement_required) {
        toast.error(`Entitlement required: ${detail.entitlement_required}`);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not create referral');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="spine-intake-modal">
        <DialogHeader>
          <DialogTitle className="cn-display text-2xl">New discharge referral</DialogTitle>
          <DialogDescription>
            PHI is encrypted at rest. This referral kicks off the placement orchestrator instantly.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2" data-testid="spine-intake-form">
          <section className="rounded-2xl border border-cn-border p-4 space-y-3 bg-cn-soft/30">
            <h3 className="text-sm font-bold uppercase tracking-wide text-cn-text-muted flex items-center gap-2">
              <Hospital className="w-4 h-4" /> Origin
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Originating organization</Label>
                <Select value={form.org_id} onValueChange={set('org_id')}>
                  <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="spine-intake-org"><SelectValue placeholder="Select hospital / system" /></SelectTrigger>
                  <SelectContent>{(orgs || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Discharge date</Label>
                <Input type="date" value={form.discharge_date} onChange={set('discharge_date')} className="mt-1.5 h-11 rounded-xl" required data-testid="spine-intake-discharge" />
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-cn-border p-4 space-y-3 bg-white">
            <h3 className="text-sm font-bold uppercase tracking-wide text-cn-text-muted flex items-center gap-2">
              <Activity className="w-4 h-4" /> Patient (PHI · encrypted)
            </h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label>MRN</Label>
                <Input value={form.patient.mrn} onChange={setPatient('mrn')} className="mt-1.5 h-11 rounded-xl" required data-testid="spine-intake-mrn" />
              </div>
              <div>
                <Label>First name</Label>
                <Input value={form.patient.first_name} onChange={setPatient('first_name')} className="mt-1.5 h-11 rounded-xl" required data-testid="spine-intake-first" />
              </div>
              <div>
                <Label>Last name</Label>
                <Input value={form.patient.last_name} onChange={setPatient('last_name')} className="mt-1.5 h-11 rounded-xl" required data-testid="spine-intake-last" />
              </div>
              <div>
                <Label>DOB</Label>
                <Input type="date" value={form.patient.dob} onChange={setPatient('dob')} className="mt-1.5 h-11 rounded-xl" required data-testid="spine-intake-dob" />
              </div>
              <div>
                <Label>ZIP-5</Label>
                <Input value={form.patient.zip5} onChange={setPatient('zip5')} maxLength={5} className="mt-1.5 h-11 rounded-xl" data-testid="spine-intake-zip" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.patient.phone} onChange={setPatient('phone')} className="mt-1.5 h-11 rounded-xl" data-testid="spine-intake-phone" />
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-cn-border p-4 space-y-3 bg-white">
            <h3 className="text-sm font-bold uppercase tracking-wide text-cn-text-muted flex items-center gap-2">
              <Stethoscope className="w-4 h-4" /> Care orders
            </h3>
            <div>
              <Label className="mb-2 block">Ordered services</Label>
              <div className="flex flex-wrap gap-2" data-testid="spine-intake-services">
                {SERVICE_OPTIONS.map((s) => (
                  <button
                    key={s.v}
                    type="button"
                    onClick={() => toggleService(s.v)}
                    data-testid={`spine-intake-svc-${s.v}`}
                    className={`px-3 py-1.5 rounded-xl text-sm font-medium border transition-colors ${form.ordered_services.includes(s.v) ? 'bg-cn-primary text-white border-cn-primary' : 'bg-cn-bg text-cn-text border-cn-border hover:border-cn-primary/40'}`}
                  >
                    {s.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Diagnosis codes (ICD-10, comma-sep)</Label>
                <Input value={form.diagnosis_codes} onChange={set('diagnosis_codes')} placeholder="I50.9, I25.10" className="mt-1.5 h-11 rounded-xl" required data-testid="spine-intake-dx" />
              </div>
              <div>
                <Label>State</Label>
                <Select value={form.state} onValueChange={set('state')}>
                  <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="spine-intake-state"><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent className="max-h-64">{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payer</Label>
                <Input value={form.payer} onChange={set('payer')} placeholder="Medicare, UnitedHealth…" className="mt-1.5 h-11 rounded-xl" data-testid="spine-intake-payer" />
              </div>
              <div>
                <Label>Payer class</Label>
                <Select value={form.payer_class} onValueChange={set('payer_class')}>
                  <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="spine-intake-payer-class"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYER_CLASSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Risk factors (comma-sep)</Label>
              <Input value={form.risk_factors} onChange={set('risk_factors')} placeholder="age_75_plus, chf, fall_risk" className="mt-1.5 h-11 rounded-xl" data-testid="spine-intake-risk" />
            </div>
          </section>

          <section>
            <Label className="mb-2 block">Urgency</Label>
            <div className="flex flex-wrap gap-2" data-testid="spine-intake-urgency-group">
              {URGENCY_OPTIONS.map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => set('urgency')(o.v)}
                  data-testid={`spine-intake-urgency-${o.v}`}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${form.urgency === o.v ? 'bg-cn-primary text-white border-cn-primary' : `${o.tone} border-transparent hover:border-cn-primary/30`}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </section>

          <button type="submit" disabled={submitting} className="cn-btn cn-btn-primary w-full justify-center !py-3" data-testid="spine-intake-submit">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Create referral <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ReferralRow = ({ r, onCancel, onShowMatch, busyId }) => {
  const ps = r.placement_summary || {};
  const services = (r.ordered_services || []).slice(0, 3).join(', ');
  const canCancel = ['matching', 'matched', 'escalated'].includes(r.status);
  return (
    <div className="cn-card rounded-2xl p-4 space-y-3" data-testid={`spine-referral-row-${r.id}`}>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={r.status} />
            <span className="text-[11px] uppercase font-semibold px-2 py-0.5 rounded-full bg-cn-soft text-cn-primary">{r.urgency}</span>
            <span className="text-xs text-cn-text-muted inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.state}</span>
            {r.discharge_date && <span className="text-xs text-cn-text-muted inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {r.discharge_date}</span>}
            {r.source && r.source !== 'manual' && (
              <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-cn-bone-200 text-cn-text-muted">{r.source}</span>
            )}
          </div>
          <div className="mt-1 text-sm text-cn-text font-medium truncate">{services || '—'}</div>
          <div className="text-xs text-cn-text-muted/80 mt-0.5">
            Patient #{(r.patient_id || '').slice(0, 8)} · {(r.diagnosis_codes || []).slice(0, 3).join(', ')}
          </div>
        </div>
        <div className="flex gap-2 text-xs flex-wrap">
          <span className="px-2 py-1 rounded-lg bg-blue-50 text-blue-700"><b>{(ps.open_offers || []).length}</b> open</span>
          <span className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700"><b>{ps.accepted ? 1 : 0}</b> accepted</span>
          <span className="px-2 py-1 rounded-lg bg-amber-50 text-amber-700"><b>{ps.declined_count || 0}</b> declined</span>
          <span className="px-2 py-1 rounded-lg bg-slate-100 text-slate-600"><b>{ps.expired_count || 0}</b> expired</span>
        </div>
      </div>

      {r.first_visit_at && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center justify-between flex-wrap gap-2" data-testid={`spine-referral-first-visit-${r.id}`}>
          <div className="text-sm">
            <CalendarClock className="inline w-4 h-4 text-emerald-700 mr-1 -mt-0.5" />
            <span className="text-emerald-900 font-semibold">First visit: {new Date(r.first_visit_at).toLocaleString()}</span>
          </div>
          <div className="text-[11px] text-emerald-800">
            {r.time_to_accept_seconds != null && (
              <span className="mr-3">Accepted in <b>{Math.round(r.time_to_accept_seconds / 60)}m</b></span>
            )}
            {r.time_to_first_visit_seconds != null && (
              <span>First visit T+<b>{Math.round(r.time_to_first_visit_seconds / 3600)}h</b></span>
            )}
          </div>
        </div>
      )}

      {/*
        Iter118 "suggested first visit" pill. Renders ONLY when the
        clinician hasn't yet explicitly committed a `first_visit_at`
        AND the backend has surfaced a system-computed suggestion from
        the latest placement. Visually softer (cyan/amber) than the
        emerald "committed" pill above so a planner can tell at a
        glance which is authoritative vs proposed.
      */}
      {!r.first_visit_at && r.suggested_first_visit_at && (() => {
        const suggested = new Date(r.suggested_first_visit_at);
        const isPast = suggested.getTime() < Date.now();
        // Past-due suggestions go amber (the clinician should have
        // committed by now); future suggestions stay cyan/neutral.
        const tone = isPast
          ? 'bg-amber-50 border-amber-200 text-amber-900'
          : 'bg-cyan-50 border-cyan-200 text-cyan-900';
        return (
          <div
            className={`rounded-xl ${tone} border px-3 py-2 flex items-center justify-between flex-wrap gap-2`}
            data-testid={`spine-referral-suggested-visit-${r.id}`}
          >
            <div className="text-sm flex items-center gap-2 flex-wrap">
              <CalendarClock className="inline w-4 h-4 -mt-0.5" />
              <span className="font-semibold">
                Suggested first visit: {suggested.toLocaleString()}
              </span>
              <span
                className="text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5"
                style={{
                  background: isPast ? 'rgba(217,119,6,0.15)' : 'rgba(8,145,178,0.15)',
                  letterSpacing: '0.08em',
                }}
                data-testid={`spine-referral-suggested-badge-${r.id}`}
              >
                Suggested
              </span>
              {isPast && (
                <span
                  className="text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5"
                  style={{
                    background: 'rgba(220,38,38,0.12)',
                    color: '#b91c1c',
                    letterSpacing: '0.08em',
                  }}
                  data-testid={`spine-referral-suggested-overdue-${r.id}`}
                >
                  Awaiting commit
                </span>
              )}
            </div>
            <div className="text-[11px] opacity-80">
              Clinician hasn&apos;t committed an exact time yet.
            </div>
          </div>
        );
      })()}

      {r.watch_summary && (() => {
        const ws = r.watch_summary;
        const tone = {
          low:       'bg-emerald-50 border-emerald-200 text-emerald-900',
          moderate:  'bg-amber-50 border-amber-200 text-amber-900',
          high:      'bg-orange-50 border-orange-200 text-orange-900',
          very_high: 'bg-red-50 border-red-200 text-red-900',
        }[ws.tier] || 'bg-slate-50 border-slate-200 text-slate-800';
        const closed = ws.status === 'closed';
        const daysToD30 = ws.day_30_at
          ? Math.max(0, Math.ceil((new Date(ws.day_30_at).getTime() - Date.now()) / 86400000))
          : null;
        const IconCmp = closed ? ShieldCheck : ShieldAlert;
        return (
          <div className={`rounded-xl border px-3 py-2 flex items-center justify-between flex-wrap gap-2 ${tone}`} data-testid={`spine-referral-watch-${r.id}`}>
            <div className="text-sm inline-flex items-center gap-2">
              <IconCmp className="w-4 h-4" />
              <span className="font-semibold">Readmission watch</span>
              <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-white/60 border border-current/20" data-testid={`spine-referral-watch-tier-${r.id}`}>
                {ws.tier}
              </span>
              {ws.score != null && (
                <span className="text-[11px] font-mono opacity-80">LACE {ws.score}/19</span>
              )}
            </div>
            <div className="text-[11px] inline-flex items-center gap-3 font-medium">
              <span data-testid={`spine-referral-watch-progress-${r.id}`}>
                {ws.touchpoints_completed}/{ws.touchpoints_total} touchpoints
              </span>
              {closed ? (
                <span className="px-2 py-0.5 rounded-full bg-white/70 border border-current/20">
                  outcome: <b>{ws.outcome || '—'}</b>
                </span>
              ) : (
                <span data-testid={`spine-referral-watch-eta-${r.id}`}>
                  day-30 in <b>{daysToD30 ?? '—'}d</b>
                </span>
              )}
            </div>
          </div>
        );
      })()}

      <div className="flex gap-2 pt-1 flex-wrap">
        <button
          onClick={() => onShowMatch(r)}
          className="cn-btn !bg-white !text-cn-primary !border !border-cn-border text-xs !py-1.5 !px-3"
          data-testid={`spine-referral-match-${r.id}`}
        >
          <Sparkles className="w-3.5 h-3.5" /> Match candidates
        </button>
        {canCancel && (
          <button
            disabled={busyId === r.id}
            onClick={() => onCancel(r)}
            className="cn-btn !bg-white !text-red-700 !border !border-red-200 hover:!bg-red-50 text-xs !py-1.5 !px-3"
            data-testid={`spine-referral-cancel-${r.id}`}
          >
            {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Ban className="w-3.5 h-3.5" /> Cancel</>}
          </button>
        )}
      </div>
    </div>
  );
};

const MatchModal = ({ open, onClose, referral, candidates, loading, llmUsed }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="spine-match-modal">
      <DialogHeader>
        <DialogTitle>Match candidates</DialogTitle>
        <DialogDescription>
          Rule-based ranking{llmUsed && <span> + LLM-assisted re-rank (Phase-0 gateway)</span>} for referral #{(referral?.id || '').slice(0, 8)}.
        </DialogDescription>
      </DialogHeader>
      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary" /></div>
      ) : !candidates.length ? (
        <div className="py-6 text-center text-cn-text-muted">No eligible clinicians in {referral?.state}.</div>
      ) : (
        <div className="space-y-2" data-testid="spine-match-list">
          {candidates.map((c, i) => (
            <div key={c.clinician_id} className="rounded-xl border border-cn-border px-3 py-2 flex items-center justify-between" data-testid={`spine-match-candidate-${c.clinician_id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-cn-text">
                  #{i + 1} · {c.first_name} {c.last_name} <code className="text-[10px] text-cn-text-muted/70">{c.clinician_id.slice(0, 8)}</code>
                </div>
                <div className="text-[11px] text-cn-text-muted">
                  state {c.state} · overlap {c.service_overlap_count} · load {c.open_offers_count} · {c.credentialing_status}
                </div>
              </div>
              {c.llm_score != null && (
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-800">
                  LLM {Math.round(c.llm_score * 100)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export default function SpineDischargePlanner() {
  const { getAuthHeader, user } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [matchTarget, setMatchTarget] = useState(null);
  const [matchData, setMatchData] = useState({ candidates: [], llm_used: false });
  const [matchLoading, setMatchLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [refsRes, orgsRes] = await Promise.all([
        axios.get(`${API}/spine/referrals?limit=100${statusFilter ? `&status=${statusFilter}` : ''}`, getAuthHeader()),
        axios.get(`${API}/organizations`, getAuthHeader()),
      ]);
      setReferrals(Array.isArray(refsRes.data) ? refsRes.data : []);
      setOrgs(Array.isArray(orgsRes.data) ? orgsRes.data : []);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402) {
        toast.error(`Entitlement disabled: ${detail?.entitlement_required || 'discharge_referral_intake'}`);
      } else {
        toast.error('Failed to load spine referrals');
      }
    } finally { setLoading(false); }
  }, [getAuthHeader, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const s = { total: referrals.length, matching: 0, matched: 0, escalated: 0, first_visit_committed: 0 };
    referrals.forEach((r) => { s[r.status] = (s[r.status] || 0) + 1; });
    return s;
  }, [referrals]);

  const handleCancel = async (r) => {
    if (!window.confirm(`Cancel referral for patient #${(r.patient_id || '').slice(0, 8)}?`)) return;
    setBusyId(r.id);
    try {
      await axios.patch(
        `${API}/spine/referrals/${r.id}`,
        { status: 'cancelled', note: 'Cancelled from discharge planner UI' },
        getAuthHeader(),
      );
      toast.success('Referral cancelled');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel');
    } finally { setBusyId(null); }
  };

  const handleShowMatch = async (r) => {
    setMatchTarget(r);
    setMatchLoading(true);
    setMatchData({ candidates: [], llm_used: false });
    try {
      const res = await axios.post(`${API}/spine/match/${r.id}`, {}, getAuthHeader());
      setMatchData({
        candidates: res.data?.candidates || [],
        llm_used: !!res.data?.llm_used,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run matcher');
    } finally { setMatchLoading(false); }
  };

  const location = useLocation();
  const navItems = plannerNavItems({ pathname: location.pathname });

  return (
    <DashboardShell
      title="Discharge orchestration"
      subtitle="Referrals from your hospital flow into AI-routed clinician placements within minutes."
      navItems={navItems}
      testId="spine-discharge-planner"
      rightSlot={
        <button onClick={() => setModalOpen(true)} className="cn-btn cn-btn-primary" data-testid="spine-new-referral-btn">
          <Plus className="w-4 h-4" /> New referral
        </button>
      }
    >
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatTile icon={Inbox} label="Total" value={stats.total} tone="bg-cn-soft text-cn-primary" testId="spine-stat-total" />
        <StatTile icon={Clock} label="Matching" value={stats.matching || 0} tone="bg-blue-100 text-blue-700" testId="spine-stat-matching" />
        <StatTile icon={CheckCircle2} label="Matched" value={stats.matched || 0} tone="bg-emerald-100 text-emerald-700" testId="spine-stat-matched" />
        <StatTile icon={CalendarClock} label="Visit booked" value={stats.first_visit_committed || 0} tone="bg-emerald-200 text-emerald-900" testId="spine-stat-first-visit" />
        <StatTile icon={AlertTriangle} label="Escalated" value={stats.escalated || 0} tone="bg-red-100 text-red-700" testId="spine-stat-escalated" />
      </div>

      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="cn-display text-xl text-cn-text">Recent referrals</h2>
        <div className="inline-flex items-center gap-1 p-1 rounded-2xl bg-cn-soft/50 border border-cn-border" data-testid="spine-status-filter">
          {['', 'matching', 'matched', 'first_visit_committed', 'escalated'].map((s) => (
            <button
              key={s || 'all'}
              onClick={() => setStatusFilter(s)}
              data-testid={`spine-filter-${s || 'all'}`}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${statusFilter === s ? 'bg-white shadow-sm text-cn-primary' : 'text-cn-text-muted hover:text-cn-text'}`}
            >
              {s ? s.replace(/_/g, ' ') : 'All'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-cn-primary" /></div>
      ) : referrals.length === 0 ? (
        <div className="cn-card rounded-3xl p-10 text-center" data-testid="spine-empty">
          <div className="w-14 h-14 rounded-2xl bg-cn-soft flex items-center justify-center mx-auto mb-4">
            <Inbox className="w-7 h-7 text-cn-primary" />
          </div>
          <h3 className="cn-display text-2xl text-cn-text mb-2">No spine referrals yet</h3>
          <p className="text-cn-text-muted max-w-md mx-auto mb-6">
            Create your first discharge referral — the orchestrator will route it to the best available clinician within seconds.
          </p>
          <button onClick={() => setModalOpen(true)} className="cn-btn cn-btn-primary" data-testid="spine-empty-new-btn">
            <Plus className="w-4 h-4" /> New referral
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="spine-referral-list">
          {referrals.map((r) => (
            <ReferralRow
              key={r.id}
              r={r}
              onCancel={handleCancel}
              onShowMatch={handleShowMatch}
              busyId={busyId}
            />
          ))}
        </div>
      )}

      <ReferralIntakeModal open={modalOpen} onClose={() => setModalOpen(false)} onCreated={load} orgs={orgs} />
      <MatchModal
        open={!!matchTarget}
        onClose={() => setMatchTarget(null)}
        referral={matchTarget}
        candidates={matchData.candidates}
        llmUsed={matchData.llm_used}
        loading={matchLoading}
      />
    </DashboardShell>
  );
}
